/**
 * AuditLog — página de visualização de logs de auditoria.
 * Acessível apenas por Coordenação/Diretoria.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Search } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { AuditEntry } from '@/lib/types';
import { downloadCSV } from '@/lib/export';

const ACOES_LABELS: Record<string, string> = {
  'contrato.criar': 'Contrato criado',
  'contrato.encerrar': 'Contrato encerrado',
  'demanda.criar': 'Demanda criada',
  'demanda.editar': 'Demanda editada',
  'demanda.aprovar': 'Demanda aprovada',
  'demanda.revisao': 'Revisão solicitada',
  'demanda.deletar': 'Demanda deletada',
  'demanda.comentar': 'Comentário adicionado',
  'escopo.status': 'Status escopo alterado',
  'escopo.aprovar': 'Escopo aprovado',
  'escopo.revisao': 'Revisão escopo',
  'pessoa.convidar': 'Cliente convidado',
  'evento.criar': 'Evento criado',
  'evento.editar': 'Evento editado',
  'inscricao.criar': 'Inscrição criada',
  'solicitante.criar': 'Solicitante criado',
  'solicitante.deletar': 'Solicitante deletado',
  'pendencia.criar': 'Pendência criada',
  'anotacao.criar': 'Nota criada',
  'anotacao.editar': 'Nota editada',
  'anotacao.deletar': 'Nota deletada',
};

const ACAO_COLORS: Record<string, string> = {
  criar: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  editar: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  aprovar: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  revisao: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  deletar: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  comentar: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  convidar: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
};

function acaoColor(acao: string): string {
  const verb = acao.split('.')[1] ?? '';
  return ACAO_COLORS[verb] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

export default function AuditLog() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [filterAcao, setFilterAcao] = useState('');
  const [filterTipo, setFilterTipo] = useState('');

  const auditQ = useQuery({
    queryKey: ['audit'],
    queryFn: () => api.audit.list().then((r) => r.data),
    staleTime: 30_000,
    enabled: user?.papel === 'Coordenação' || user?.papel === 'Diretoria',
  });

  const entries = auditQ.data ?? [];

  const acoesUnicas = useMemo(() =>
    [...new Set(entries.map((e) => e.acao))].sort(),
    [entries],
  );

  const tiposUnicos = useMemo(() =>
    [...new Set(entries.map((e) => e.tipoRecurso))].sort(),
    [entries],
  );

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterAcao && e.acao !== filterAcao) return false;
      if (filterTipo && e.tipoRecurso !== filterTipo) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          e.usuario.toLowerCase().includes(q) ||
          e.email.toLowerCase().includes(q) ||
          e.detalhes.toLowerCase().includes(q) ||
          e.acao.toLowerCase().includes(q) ||
          e.idRecurso.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [entries, search, filterAcao, filterTipo]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const handleExport = () => {
    const headers = ['Data', 'Ação', 'Usuário', 'Email', 'Papel', 'Tipo Recurso', 'ID Recurso', 'Detalhes'];
    const rows = filtered.map((e) => [
      formatDate(e.data), e.acao, e.usuario, e.email, e.papel, e.tipoRecurso, e.idRecurso, e.detalhes,
    ]);
    downloadCSV(`audit-log-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  if (user?.papel !== 'Coordenação' && user?.papel !== 'Diretoria') {
    return <div className="p-8 text-center text-gray-400">Acesso restrito a Coordenação e Diretoria.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-4">
      <PageHeader title="Audit Log" subtitle={`${entries.length} registros`} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por usuário, email, detalhes..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterAcao}
          onChange={(e) => setFilterAcao(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
        >
          <option value="">Todas as ações</option>
          {acoesUnicas.map((a) => (
            <option key={a} value={a}>{ACOES_LABELS[a] || a}</option>
          ))}
        </select>
        <select
          value={filterTipo}
          onChange={(e) => setFilterTipo(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
        >
          <option value="">Todos os tipos</option>
          {tiposUnicos.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Download className="w-4 h-4" /> CSV
        </button>
      </div>

      {/* Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-2.5">Data</th>
                <th className="px-4 py-2.5">Ação</th>
                <th className="px-4 py-2.5">Usuário</th>
                <th className="px-4 py-2.5">Papel</th>
                <th className="px-4 py-2.5">Recurso</th>
                <th className="px-4 py-2.5">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {auditQ.isLoading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Carregando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Nenhum registro encontrado.</td></tr>
              ) : (
                filtered.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-2.5 text-[11px] text-gray-500 whitespace-nowrap">
                      {formatDate(e.data)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${acaoColor(e.acao)}`}>
                        {ACOES_LABELS[e.acao] || e.acao}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-xs font-medium">{e.usuario}</div>
                      <div className="text-[10px] text-gray-400">{e.email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-gray-500">{e.papel}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                        {e.tipoRecurso}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-gray-600 dark:text-gray-400 max-w-[300px] truncate" title={e.detalhes}>
                      {e.detalhes || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
            {filtered.length} de {entries.length} registros
          </div>
        )}
      </div>
    </div>
  );
}
